import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, Compass, MapPin, MessageCircle, Search } from 'lucide-react';
import { GlassCard } from '@/components/shared/GlassCard';
import { PageMeta } from '@/components/shared/PageMeta';
import { Button } from '@/components/ui/button';
import { brand } from '@/lib/theme/tokens';
import { photo, unsplash } from '@/lib/utils/img';
import { fadeUp, inViewOnce, pageTransition, staggerContainer } from '@/lib/motion/variants';

const SUGGESTIONS: { to: string; label: string; sub: string; color: string }[] = [
  { to: '/',                            label: 'Home',          sub: 'Start fresh',                color: brand.teal },
  { to: '/search',                      label: 'Search trips',  sub: '9 experiences',              color: brand.saffron },
  { to: '/activities/tarsar-marsar-trek', label: 'Tarsar Marsar', sub: 'Bestseller · 7 days',     color: brand.chinar },
  { to: '/stays',                       label: 'Stays',         sub: '17 hotels + homestays',      color: brand.cobalt },
  { to: '/clubs',                       label: 'Clubs',         sub: 'Locals show up daily',       color: brand.ok },
  { to: '/community',                   label: 'Community',     sub: 'Match with companions',      color: brand.chinar },
];

export function NotFoundPage() {
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  }

  return (
    <motion.div variants={pageTransition} initial="initial" animate="animate" exit="exit">
      <PageMeta
        title="Page not found"
        description="The path you followed doesn't exist on Uncharted Odyssey. Here's where to go next."
      />

      <section className="mx-auto max-w-7xl px-4 pt-10 md:px-6 md:pt-14">
        <div className="relative overflow-hidden rounded-[20px] border border-glass-edge bg-glass md:rounded-[28px]">
          <img
            src={unsplash(photo.alpineRidge, { w: 2000 })}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover opacity-40"
          />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(60% 60% at 18% 20%, ${brand.teal}1f 0%, transparent 70%), linear-gradient(180deg, rgba(11,13,18,0.4) 0%, rgba(11,13,18,0.85) 100%)`,
            }}
          />

          <div className="relative flex flex-col gap-6 p-6 md:p-12">
            <div
              className="inline-flex items-center gap-2 self-start rounded-full border border-white/15 bg-black/30 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.25em] text-white backdrop-blur"
            >
              <Compass size={11} /> 404 · off the map
            </div>
            <h1 className="font-display max-w-3xl text-4xl font-semibold leading-[1.02] tracking-tight text-white md:text-6xl">
              That path is{' '}
              <em
                className="font-medium italic"
                style={{
                  display: 'inline-block',
                  lineHeight: 1.1,
                  paddingBottom: '0.1em',
                  backgroundImage: `linear-gradient(135deg, ${brand.teal}, ${brand.saffron})`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                }}
              >
                uncharted.
              </em>
            </h1>
            <p className="max-w-2xl text-[14px] leading-relaxed text-white/85 md:text-[16px]">
              The page you're looking for doesn't exist — or never did. No worries. The valley is big. Here's where you can pick the trail back up.
            </p>

            <form onSubmit={submit} className="flex w-full max-w-xl flex-wrap items-center gap-2 rounded-full border border-white/20 bg-black/40 p-1.5 backdrop-blur-md sm:flex-nowrap">
              <div className="flex flex-1 items-center gap-2 pl-3">
                <Search size={14} className="text-white/70" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search trips, stays, guides…"
                  className="w-full bg-transparent text-[13px] text-white outline-none placeholder:text-white/55"
                />
              </div>
              <Button type="submit" size="sm" className="shrink-0">
                Search <ArrowRight size={12} />
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-10 max-w-7xl px-4 md:px-6">
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-ink-mute">
          Try one of these
        </div>
        <h2 className="font-display mt-2 text-2xl font-semibold tracking-tight text-ink md:text-3xl">
          Where you can{' '}
          <em
            className="font-medium italic"
            style={{
              display: 'inline-block',
              lineHeight: 1.1,
              paddingBottom: '0.1em',
              backgroundImage: `linear-gradient(135deg, ${brand.chinar}, ${brand.saffron})`,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              color: 'transparent',
            }}
          >
            actually go.
          </em>
        </h2>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={inViewOnce}
          variants={staggerContainer}
          className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          {SUGGESTIONS.map((s) => (
            <motion.div key={s.to} variants={fadeUp}>
              <Link to={s.to} className="block">
                <GlassCard interactive className="flex items-center gap-3 p-4" style={{ borderColor: `${s.color}40` }}>
                  <span
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
                    style={{ background: `${s.color}1c`, color: s.color, border: `1px solid ${s.color}55` }}
                  >
                    <MapPin size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-ink">{s.label}</div>
                    <div className="mt-0.5 text-[11px] text-ink-mute">{s.sub}</div>
                  </div>
                  <ArrowRight size={13} className="text-ink-mute" />
                </GlassCard>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-2 text-[12px] text-ink-mute">
          <span>Followed a broken link?</span>
          <Link
            to="/business/contact"
            className="inline-flex items-center gap-1 font-semibold text-ink underline-offset-2 hover:underline"
          >
            <MessageCircle size={11} /> Tell us where you came from
          </Link>
        </div>
      </section>

      <div className="h-16" />
    </motion.div>
  );
}
